import { useEffect, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getManualHsCodesRequest, clearManualHsCodes } from 'redux/reducers/hscodes/reducer';
import { getManualHsCodesItems, getManualHsCodesLoading } from 'redux/reducers/hscodes/selectors';
import { AppDispatch } from 'redux/store';
import { IHsCodeItem } from 'redux/reducers/hscodes/types';

export const useManualHsCodesRedux = () => {
	const dispatch = useDispatch<AppDispatch>();
	const options = useSelector(getManualHsCodesItems);
	const loading = useSelector(getManualHsCodesLoading);

	// Стек выбранных элементов для отслеживания иерархии
	const [hsCodeStack, setHsCodeStack] = useState<IHsCodeItem[]>([]);
	// Финальный выбранный HS код (когда длина >= 7)
	const [finalHsCode, setFinalHsCode] = useState<IHsCodeItem | null>(null);

	// Отладочная информация
	useEffect(() => {
		console.log('Manual HS Codes Debug:', {
			options,
			loading,
			hsCodeStack,
			finalHsCode,
		});
	}, [options, loading, hsCodeStack, finalHsCode]);

	// Очищаем состояние при размонтировании
	useEffect(() => {
		return () => {
			dispatch(clearManualHsCodes());
		};
	}, [dispatch]);

	const selectHsCode = useCallback(
		(hsCode: IHsCodeItem) => {
			console.log('Selecting HS Code:', hsCode);
			if (hsCode.code.length >= 7) {
				// Это финальный HS код
				setFinalHsCode(hsCode);
			} else {
				// Добавляем к стеку и загружаем дочерние элементы
				setHsCodeStack((prev) => [...prev, hsCode]);
				console.log('Loading child options for ID:', hsCode.id);
				dispatch(getManualHsCodesRequest(hsCode.id));
			}
		},
		[dispatch],
	);

	const goBack = useCallback(() => {
		if (hsCodeStack.length > 0) {
			const newStack = [...hsCodeStack];
			newStack.pop();
			setHsCodeStack(newStack);
			setFinalHsCode(null);

			// Загружаем предыдущий уровень
			const parentId = newStack.length > 0 ? newStack[newStack.length - 1].id : 0;
			console.log('Going back, loading parent ID:', parentId);
			dispatch(getManualHsCodesRequest(parentId));
		}
	}, [hsCodeStack, dispatch]);

	const reset = useCallback(() => {
		setHsCodeStack([]);
		setFinalHsCode(null);
		console.log('Resetting, loading root options');
		dispatch(getManualHsCodesRequest(0));
	}, [dispatch]);

	const currentPath = useMemo(() => {
		return hsCodeStack.map((item) => item.code).join(' > ');
	}, [hsCodeStack]);

	// Текущий выбранный элемент для отображения в поле ввода
	const currentSelection = useMemo(() => {
		if (finalHsCode) {
			return finalHsCode;
		}
		if (hsCodeStack.length > 0) {
			return hsCodeStack[hsCodeStack.length - 1];
		}
		return null;
	}, [hsCodeStack, finalHsCode]);

	// Функция для загрузки опций по ID
	const loadOptions = useCallback(
		(id: number) => {
			console.log('Loading options for ID:', id);
			dispatch(getManualHsCodesRequest(id));
		},
		[dispatch],
	);

	return {
		options,
		loading,
		hsCodeStack,
		finalHsCode,
		currentPath,
		currentSelection,
		selectHsCode,
		goBack,
		reset,
		loadOptions,
		canGoBack: hsCodeStack.length > 0,
		isComplete: finalHsCode !== null,
	};
};
